import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../../services/i18n/i18nSlice'
import { setLoading } from '../../../services/global/globalSlice'
import { fetchCreateQuestion, fetchDeleteQuestion, fetchDownloadQuestionBank, fetchEditQuestion, fetchQuestionById, fetchQuestions } from '../QuestionBankApi'
import QuestionBankQuestionEditor from './QuestionBankQuestionEditor'
import QuestionsUploader from '../../../components/_questions/QuestionsUploader'
import ConfirmModal from '../../../components/_modals/ConfirmModal'
import upload_img from '../../../assets/images/upload_white.svg'
import upload_blue_img from '../../../assets/images/upload_blue.svg'
import download_img from '../../../assets/images/download.svg'
import excel_img from '../../../assets/images/questions/excel.svg'
import excel_template from '../../../assets/questions.xlsx'
import trash_img from '../../../assets/images/trash.svg'

const BLANK_QUESTION = { question_type: 'base', text: '', answers: [] }

export default function QuestionsTab(props) {
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const subjects = props.subjects
    const topics = props.topics
    const [subjectFilter, setSubjectFilter] = useState('')
    const [topicFilter, setTopicFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [idSearch, setIdSearch] = useState('')
    const [searchingById, setSearchingById] = useState(false)

    const [questions, setQuestions] = useState([])
    const [fetchState, setFetchState] = useState({ start_cursor: null })

    const [showEditor, setShowEditor] = useState(false)
    const [isNew, setIsNew] = useState(true)
    const [selectedQuestion, setSelectedQuestion] = useState(null)

    const [showUploadModal, setShowUploadModal] = useState(false)
    const [showHelpCard, setShowHelpCard] = useState(false)

    const [confirmModal, setConfirmModal] = useState({
        show: false, title: '', text: '', onClose: null, onConfirm: null, confirmCta: '', closeCta: ''
    })

    const topicsForFilter = useMemo(() => {
        if (!subjectFilter) return topics
        return topics.filter(topic => topic.subject_id === subjectFilter)
    }, [subjectFilter, topics])

    const topicsById = useMemo(() => {
        const map = {}
        topics?.forEach(topic => { map[topic.topic_id] = topic.topic_name })
        return map
    }, [topics])

    const subjectNameByTopicId = useMemo(() => {
        const subjectNamesById = {}
        subjects?.forEach(s => { subjectNamesById[s.subject_id] = s.subject_name })
        const map = {}
        topics?.forEach(topic => { map[topic.topic_id] = subjectNamesById[topic.subject_id] || '' })
        return map
    }, [topics, subjects])

    const filteredQuestions = useMemo(() => {
        let _questions = questions.filter(q => !q.is_deleted)
        if (typeFilter) _questions = _questions.filter(q => q.question_type === typeFilter)
        return _questions
    }, [questions, typeFilter])

    const hasActiveFilters = !!(subjectFilter || topicFilter || typeFilter)

    const loadQuestions = async (reset = false) => {
        dispatch(setLoading(true))
        const _params = {
            limit: 20,
            ...(topicFilter && { topic_id: topicFilter }),
            ...(!topicFilter && subjectFilter && { subject_id: subjectFilter }),
            ...(!reset && fetchState?.start_cursor && { start_cursor: fetchState.start_cursor })
        }
        const response = await fetchQuestions(_params)
        if (response.success) {
            setQuestions(reset ? response.data?.questions || [] : [...questions, ...(response.data?.questions || [])])
            setFetchState({ start_cursor: response.data?.next_cursor })
        } else {
            toast.error(t.error.error_loading)
        }
        dispatch(setLoading(false))
    }

    useEffect(() => { loadQuestions(true) }, [topicFilter, subjectFilter])

    const handleSearchById = async () => {
        const targetId = idSearch.trim()
        if (!targetId) return
        setSearchingById(true)
        dispatch(setLoading(true))
        const response = await fetchQuestionById(targetId)
        dispatch(setLoading(false))
        setSearchingById(false)
        if (response.success) {
            openEditQuestion(response.data)
        } else {
            toast.error(t.question_bank.question_id_not_found)
        }
    }

    const handleIdSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSearchById()
        }
    }

    const handleSubjectFilterChange = (value) => {
        setSubjectFilter(value)
        setTopicFilter('')
    }

    const clearFilters = () => {
        setSubjectFilter('')
        setTopicFilter('')
        setTypeFilter('')
    }

    const openNewQuestion = () => {
        setSelectedQuestion({ ...BLANK_QUESTION, topic_id: topicFilter || '' })
        setIsNew(true)
        setShowEditor(true)
    }

    const openEditQuestion = (question) => {
        setSelectedQuestion(structuredClone(question))
        setIsNew(false)
        setShowEditor(true)
    }

    const closeEditor = () => {
        setShowEditor(false)
        setSelectedQuestion(null)
    }

    const handleDataChange = (data) => {
        setSelectedQuestion(data)
    }

    const validateQuestion = (question) => {
        if (!question.topic_id) {
            toast.error(t.question_bank.topic_required_for_question)
            return false
        }
        if (!question.text) {
            toast.error(t.question.required_text)
            return false
        }
        if (question.question_type !== 'block') {
            const min_answers = question.question_type === 'fill' ? 1 : 2
            if ((question.answers?.length || 0) < min_answers) {
                toast.error(question.question_type === 'fill' ? t.question.min_answers_fill : t.question.min_answers)
                return false
            }
            const correct_answers = question.answers.filter(a => a.is_correct_answer === true)
            if (question.question_type === 'base' && correct_answers.length > 1) {
                toast.error(t.question.only_one_correct)
                return false
            }
            if (question.question_type !== 'survey' && correct_answers.length === 0) {
                toast.error(t.question.at_least_one_correct)
                return false
            }
        } else {
            if ((question.blocks?.length || 0) < 1) {
                toast.error(t.question.min_block)
                return false
            }
            for (const block of question.blocks) {
                if (!block.text) {
                    toast.error(t.question.required_text_block)
                    return false
                }
                const correct_answers = block.answers.filter(a => a.is_correct_answer === true)
                if (correct_answers.length > 1) {
                    toast.error(t.question.only_one_correct)
                    return false
                }
            }
        }
        return true
    }

    const saveQuestion = async () => {
        if (!validateQuestion(selectedQuestion)) return false
        dispatch(setLoading(true))
        const response = isNew
            ? await fetchCreateQuestion(selectedQuestion)
            : await fetchEditQuestion(selectedQuestion)
        if (response.success) {
            await loadQuestions(true)
        } else {
            toast.error(t.question.error_saving_questions)
        }
        dispatch(setLoading(false))
        return response.success
    }

    const handleSaveAndClose = async () => {
        if (await saveQuestion()) closeEditor()
    }

    const handleSaveAndNew = async () => {
        const savedTopicId = selectedQuestion?.topic_id
        if (await saveQuestion()) {
            setSelectedQuestion({ ...BLANK_QUESTION, topic_id: savedTopicId })
            setIsNew(true)
        }
    }

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, show: false })

    const performDeleteQuestion = async (question_id, force_delete = false) => {
        dispatch(setLoading(true))
        const response = await fetchDeleteQuestion(question_id, force_delete)
        dispatch(setLoading(false))
        if (response.success) {
            toast.success(t.question_bank.deleted_question)
            closeConfirmModal()
            if (showEditor) closeEditor()
            await loadQuestions(true)
            return
        }
        if (!force_delete && response.error?.includes('used in')) {
            setConfirmModal({
                show: true,
                title: t.question_bank.force_delete_title,
                text: t.question_bank.force_delete_text,
                onClose: closeConfirmModal,
                onConfirm: () => performDeleteQuestion(question_id, true),
                confirmCta: t.question_bank.force_delete_cta,
                closeCta: t.modals.no_exit
            })
            return
        }
        toast.error(t.error.error_loading)
    }

    const handleDeleteQuestion = (question_id) => {
        setConfirmModal({
            show: true,
            title: t.question_bank.delete_question_title,
            text: t.question_bank.delete_question_text,
            onClose: closeConfirmModal,
            onConfirm: () => performDeleteQuestion(question_id, false),
            confirmCta: t.actions.delete,
            closeCta: t.modals.no_exit
        })
    }

    const handleEditorRemove = () => {
        if (!selectedQuestion?.question_id) {
            closeEditor()
            return
        }
        handleDeleteQuestion(selectedQuestion.question_id)
    }

    const handleImportComplete = () => {
        setShowUploadModal(false)
        loadQuestions(true)
    }

    const downloadQuestions = async () => {
        dispatch(setLoading(true))
        const response = await fetchDownloadQuestionBank({
            ...(subjectFilter && { subject_id: subjectFilter }),
            ...(topicFilter && { topic_id: topicFilter }),
            ...(typeFilter && { question_type: typeFilter })
        })
        if (response.success) {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `question_bank_${new Date().toLocaleDateString("it-IT")}.xlsx`)
            document.body.appendChild(link)
            link.click()
        } else {
            toast.error(t.question.error_while_downloading)
        }
        dispatch(setLoading(false))
    }

    return (
        <section>
            <div className="info_card mb-3">
                <h2>{t.question_bank.search_filters}</h2>
                <div className="d-flex align-items-end flex-wrap mt-2">
                    <Form.Group className="mb-2 me-2" controlId="question_subject_filter">
                        <Form.Label>{t.question_bank.subject}</Form.Label>
                        <Form.Select value={subjectFilter} onChange={(e) => handleSubjectFilterChange(e.target.value)}>
                            <option value="">{t.question_bank.all_subjects}</option>
                            {subjects?.map(s => (
                                <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2 me-2" controlId="question_topic_filter">
                        <Form.Label>{t.question_bank.topic}</Form.Label>
                        <Form.Select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)}>
                            <option value="">{t.question_bank.all_topics}</option>
                            {topicsForFilter?.map(topic => (
                                <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2 me-2" controlId="question_type_filter">
                        <Form.Label>{t.question.question_type}</Form.Label>
                        <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="">{t.question_bank.all_types}</option>
                            <option value="base">{t.question.single_choice}</option>
                            <option value="multi">{t.question.multiple_choice}</option>
                            <option value="block">{t.question.block_question}</option>
                            <option value="survey">{t.question.survey_question}</option>
                            <option value="fill">{t.question.fill_question}</option>
                        </Form.Select>
                    </Form.Group>
                    {hasActiveFilters ?
                        <button type="button" className="secondary mb-2" onClick={clearFilters}>{t.question_bank.clear_filters}</button>
                    : null}
                </div>
                <div className="d-flex align-items-end flex-wrap mt-2">
                    <Form.Group className="mb-2 me-2" controlId="question_id_search">
                        <Form.Label>{t.question_bank.search_by_id_label}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t.question_bank.search_by_id_placeholder}
                            value={idSearch}
                            onChange={(e) => setIdSearch(e.target.value)}
                            onKeyDown={handleIdSearchKeyDown}
                        />
                    </Form.Group>
                    <button type="button" className="secondary mb-2" disabled={!idSearch.trim() || searchingById} onClick={handleSearchById}>
                        {searchingById ? t.question_bank.searching_by_id : t.question_bank.search_by_id_cta}
                    </button>
                </div>
            </div>

            <div className="d-flex justify-content-end flex-wrap mb-3">
                <button type="button" className="secondary mb-2 me-2" onClick={downloadQuestions}>
                    {t.question.download_questions}
                    <img src={download_img} className="ms-1" alt="" />
                </button>
                <button type="button" className="secondary mb-2 me-2" onClick={() => setShowHelpCard(!showHelpCard)}>
                    {t.question.excel_import}
                    <img src={upload_blue_img} className="ms-1" alt="" />
                </button>
                <button className="mb-2" onClick={openNewQuestion}>{t.question.new_question}</button>
            </div>

            {showHelpCard ?
                <div className="info_card mb-3">
                    <h2>{t.question.question_adding}</h2>
                    <div className="d-flex mt-3 flex-wrap">
                        <button onClick={() => setShowUploadModal(true)}>
                            {t.question.excel_import}
                            <img src={upload_img} className="ms-1" alt="" />
                        </button>
                    </div>
                    <div className="info_card mt-3">
                        <h3><img src={excel_img} className="me-1" alt="" />{t.question.template_excel}</h3>
                        <p>{t.question.template_excel_text}</p>
                        <a href={excel_template} download>
                            <button className="secondary">
                                {t.question.download_template}
                                <img src={download_img} className="ms-1" alt="" />
                            </button>
                        </a>
                    </div>
                </div>
            : null}

            <QuestionsUploader
                show={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                endpoint="/admin/add_new_questions_from_excel"
                onUploadComplete={handleImportComplete}
            />

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t.question_bank.question_text}</th>
                            <th>{t.question_bank.subject_topic}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredQuestions?.length ? filteredQuestions.map((q) => (
                            <tr key={q.question_id}>
                                <td>{q.text}</td>
                                <td>
                                    <div className="question_bank_tags">
                                        {subjectNameByTopicId[q.topic_id] ?
                                            <span className="question_bank_tag question_bank_tag_subject">{subjectNameByTopicId[q.topic_id]}</span>
                                        : null}
                                        {topicsById[q.topic_id] ?
                                            <span className="question_bank_tag question_bank_tag_topic">{topicsById[q.topic_id]}</span>
                                        : null}
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex justify-content-end">
                                        <button className="secondary me-2" onClick={() => openEditQuestion(q)}>{t.actions.open}</button>
                                        <button className="secondary" onClick={() => handleDeleteQuestion(q.question_id)}>
                                            <img src={trash_img} alt="" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={3} className="text-center">{t.question_bank.no_questions}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {fetchState?.start_cursor ?
                <div className="text-center">
                    <button className="secondary" onClick={() => loadQuestions(false)}>{t.question_bank.load_more}</button>
                </div>
            : null}

            <QuestionBankQuestionEditor
                show={showEditor}
                isNew={isNew}
                questionData={selectedQuestion}
                subjects={subjects}
                topics={topics}
                onDataChange={handleDataChange}
                onClose={closeEditor}
                onSaveAndClose={handleSaveAndClose}
                onSaveAndNew={handleSaveAndNew}
                onRemove={handleEditorRemove}
            />

            <ConfirmModal props={confirmModal} />
        </section>
    )
}
